// Public booking page - visible to anyone at username.cal.larryshelp.org
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getPublicBookingPage,
  getPublicEventType,
  getAvailableSlots,
  getPublicStaff,
  createBooking
} from '../../services/scheduling-api';
import { EventType, AvailableSlot, BookingPageData, PublicStaffMember } from '../../types/scheduling';

const PublicBookingPage: React.FC = () => {
  const { subdomain, eventSlug } = useParams<{ subdomain?: string; eventSlug?: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageData, setPageData] = useState<BookingPageData | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [staffMembers, setStaffMembers] = useState<PublicStaffMember[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<PublicStaffMember | null>(null);
  const [staffLoading, setStaffLoading] = useState(false);
  const [step, setStep] = useState<'events' | 'staff' | 'slots' | 'form' | 'confirmed'>('events');

  // Form state
  const [inviteeName, setInviteeName] = useState('');
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [inviteeNotes, setInviteeNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bookingConfirmation, setBookingConfirmation] = useState<any>(null);

  // Date navigation
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  // Extract subdomain from URL if not in params
  const getSubdomain = (): string | null => {
    if (subdomain) return subdomain;

    const host = window.location.hostname;
    if (host.includes('.cal.larryshelp.org')) {
      return host.split('.cal.')[0];
    }
    if (host.includes('.cal.larryshelp.local')) {
      return host.split('.cal.')[0];
    }
    return null;
  };

  useEffect(() => {
    loadBookingPage();
  }, [subdomain, eventSlug]);

  const loadStaffAndProceed = async (subdomainToUse: string, eventType: EventType) => {
    try {
      setStaffLoading(true);
      const staffData = await getPublicStaff(subdomainToUse);
      setStaffMembers(staffData.staff);

      if (staffData.staff.length > 0) {
        setStep('staff');
      } else {
        setStep('slots');
        await loadSlots(subdomainToUse, eventType.slug);
      }
    } catch {
      // If staff fetch fails, skip staff step and go straight to slots
      setStep('slots');
      await loadSlots(subdomainToUse, eventType.slug);
    } finally {
      setStaffLoading(false);
    }
  };

  const loadBookingPage = async () => {
    const subdomainToUse = getSubdomain();

    if (!subdomainToUse) {
      setError('Invalid booking URL');
      setLoading(false);
      return;
    }

    try {
      if (eventSlug) {
        // Load specific event type
        const data = await getPublicEventType(subdomainToUse, eventSlug);
        setPageData({
          user: data.user,
          eventTypes: [data.eventType],
          hideBranding: data.hideBranding
        });
        setSelectedEvent(data.eventType);
        await loadStaffAndProceed(subdomainToUse, data.eventType);
      } else {
        // Load all event types
        const data = await getPublicBookingPage(subdomainToUse);
        setPageData(data);

        if (data.eventTypes.length === 1) {
          setSelectedEvent(data.eventTypes[0]);
          await loadStaffAndProceed(subdomainToUse, data.eventTypes[0]);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load booking page');
    } finally {
      setLoading(false);
    }
  };

  const loadSlots = async (sub: string, slug: string, staffId?: number) => {
    try {
      const startDate = currentWeekStart.toISOString().split('T')[0];
      const endDate = new Date(currentWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const data = await getAvailableSlots(sub, slug, startDate, endDate, timezone, staffId);
      setAvailableSlots(data.availableSlots);
    } catch (err) {
      console.error('Failed to load slots:', err);
    }
  };

  const handleSelectEvent = async (eventType: EventType) => {
    setSelectedEvent(eventType);
    setSelectedStaff(null);

    const subdomainToUse = getSubdomain();
    if (subdomainToUse) {
      await loadStaffAndProceed(subdomainToUse, eventType);
    }
  };

  const handleSelectStaff = async (staff: PublicStaffMember | null) => {
    setSelectedStaff(staff);
    setStep('slots');

    const subdomainToUse = getSubdomain();
    if (subdomainToUse && selectedEvent) {
      await loadSlots(subdomainToUse, selectedEvent.slug, staff?.id);
    }
  };

  const handleSelectSlot = (slot: AvailableSlot) => {
    setSelectedSlot(slot);
    setStep('form');
  };

  const handleSubmitBooking = async () => {
    if (!selectedSlot || !selectedEvent || !inviteeName || !inviteeEmail) return;

    const subdomainToUse = getSubdomain();
    if (!subdomainToUse) return;

    setSubmitting(true);

    try {
      const confirmation = await createBooking(subdomainToUse, selectedEvent.slug, {
        eventTypeId: selectedEvent.id,
        startTime: selectedSlot.startDateTime,
        inviteeName,
        inviteeEmail,
        inviteeTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        inviteeNotes: inviteeNotes || undefined,
        staffMemberId: selectedStaff?.id
      });

      setBookingConfirmation(confirmation);
      setStep('confirmed');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrevWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (newStart >= today) {
      setCurrentWeekStart(newStart);
      const sub = getSubdomain();
      if (sub && selectedEvent) {
        loadSlots(sub, selectedEvent.slug, selectedStaff?.id);
      }
    }
  };

  const handleNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);

    const sub = getSubdomain();
    if (sub && selectedEvent) {
      loadSlots(sub, selectedEvent.slug, selectedStaff?.id);
    }
  };

  const formatSlotTime = (slot: AvailableSlot) => {
    const date = new Date(slot.startDateTime);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Group slots by date
  const slotsByDate = availableSlots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {} as Record<string, AvailableSlot[]>);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <h1 style={{ color: '#dc2626' }}>Booking Page Not Found</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (!pageData) return null;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          {pageData.user.avatarUrl && (
            <img
              src={pageData.user.avatarUrl}
              alt={pageData.user.displayName}
              style={{ width: '80px', height: '80px', borderRadius: '50%', marginBottom: '15px' }}
            />
          )}
          <h1 style={{ margin: '0 0 5px', fontSize: '1.5rem' }}>{pageData.user.displayName}</h1>
          {pageData.user.bio && (
            <p style={{ margin: 0, color: '#666' }}>{pageData.user.bio}</p>
          )}
          {pageData.user.welcomeMessage && (
            <p style={{ margin: '15px 0 0', color: '#374151' }}>{pageData.user.welcomeMessage}</p>
          )}
        </div>

        {/* Event Selection */}
        {step === 'events' && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ margin: '0 0 20px' }}>Select an Event</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {pageData.eventTypes.map(et => (
                <button
                  key={et.id}
                  onClick={() => handleSelectEvent(et)}
                  style={{
                    padding: '20px',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderLeft: `4px solid ${et.color || '#3b82f6'}`,
                    borderRadius: '8px',
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                >
                  <p style={{ margin: 0, fontWeight: '600', fontSize: '1.125rem' }}>{et.title}</p>
                  <p style={{ margin: '8px 0 0', color: '#666' }}>{et.durationMinutes} minutes</p>
                  {et.description && (
                    <p style={{ margin: '8px 0 0', color: '#666', fontSize: '0.875rem' }}>{et.description}</p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Staff Selection */}
        {step === 'staff' && selectedEvent && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
              {pageData.eventTypes.length > 1 && (
                <button
                  onClick={() => { setStep('events'); setSelectedEvent(null); setSelectedStaff(null); }}
                  style={{ padding: '8px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  ←
                </button>
              )}
              <div>
                <h2 style={{ margin: 0 }}>Choose Your Provider</h2>
                <p style={{ margin: '5px 0 0', color: '#666' }}>{selectedEvent.title} — {selectedEvent.durationMinutes} minutes</p>
              </div>
            </div>

            {staffLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <p>Loading providers...</p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '16px',
                padding: '20px 0'
              }}>
                {staffMembers.map(staff => (
                  <button
                    key={staff.id}
                    onClick={() => handleSelectStaff(staff)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '24px 16px',
                      border: selectedStaff?.id === staff.id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: selectedStaff?.id === staff.id ? '#eff6ff' : 'white',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    {staff.avatarUrl ? (
                      <img
                        src={staff.avatarUrl}
                        alt={staff.name}
                        style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          marginBottom: '12px'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        marginBottom: '12px'
                      }}>
                        {staff.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                    )}
                    <p style={{ margin: 0, fontWeight: '600' }}>{staff.name}</p>
                    {staff.title && <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: '#666' }}>{staff.title}</p>}
                    {staff.bio && <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: '#888', lineHeight: '1.3' }}>{staff.bio.length > 80 ? staff.bio.substring(0, 80) + '...' : staff.bio}</p>}
                  </button>
                ))}
                {/* Any Available option */}
                <button
                  onClick={() => handleSelectStaff(null)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '24px 16px',
                    border: selectedStaff === null ? '1px solid #e5e7eb' : '1px solid #e5e7eb',
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    textAlign: 'center'
                  }}
                >
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: '#9ca3af',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    ?
                  </div>
                  <p style={{ margin: 0, fontWeight: '600' }}>Any Available</p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: '#666' }}>First available provider</p>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Slot Selection */}
        {step === 'slots' && selectedEvent && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
              {(staffMembers.length > 0 || pageData.eventTypes.length > 1) && (
                <button
                  onClick={() => {
                    if (staffMembers.length > 0) {
                      setStep('staff');
                    } else {
                      setStep('events');
                      setSelectedEvent(null);
                    }
                  }}
                  style={{ padding: '8px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  ←
                </button>
              )}
              <div>
                <h2 style={{ margin: 0 }}>{selectedEvent.title}</h2>
                <p style={{ margin: '5px 0 0', color: '#666' }}>{selectedEvent.durationMinutes} minutes</p>
              </div>
            </div>

            {/* Week navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <button
                onClick={handlePrevWeek}
                style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                ← Previous
              </button>
              <span style={{ fontWeight: '500' }}>
                {formatDate(currentWeekStart.toISOString())}
              </span>
              <button
                onClick={handleNextWeek}
                style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Next →
              </button>
            </div>

            {Object.keys(slotsByDate).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <p>No available slots this week.</p>
                <p style={{ fontSize: '0.875rem' }}>Try selecting a different week.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '15px' }}>
                {Object.entries(slotsByDate).map(([date, slots]) => (
                  <div key={date}>
                    <p style={{ margin: '0 0 10px', fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                      {formatDate(date)}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {slots.map((slot, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSelectSlot(slot)}
                          style={{
                            padding: '10px',
                            backgroundColor: pageData.user.brandColor || '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          {formatSlotTime(slot)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Booking Form */}
        {step === 'form' && selectedEvent && selectedSlot && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <button
              onClick={() => setStep('slots')}
              style={{ padding: '8px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', marginBottom: '20px' }}
            >
              ← Back
            </button>

            <div style={{ padding: '15px', backgroundColor: '#f3f4f6', borderRadius: '8px', marginBottom: '25px' }}>
              <p style={{ margin: 0, fontWeight: '500' }}>{selectedEvent.title}</p>
              <p style={{ margin: '5px 0 0', color: '#666' }}>
                {new Date(selectedSlot.startDateTime).toLocaleString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </p>
            </div>

            <h3 style={{ margin: '0 0 20px' }}>Your Details</h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Name *</label>
              <input
                type="text"
                value={inviteeName}
                onChange={e => setInviteeName(e.target.value)}
                placeholder="Your name"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Email *</label>
              <input
                type="email"
                value={inviteeEmail}
                onChange={e => setInviteeEmail(e.target.value)}
                placeholder="you@example.com"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Notes (optional)</label>
              <textarea
                value={inviteeNotes}
                onChange={e => setInviteeNotes(e.target.value)}
                placeholder="Anything you'd like us to know..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <button
              onClick={handleSubmitBooking}
              disabled={!inviteeName || !inviteeEmail || submitting}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: (!inviteeName || !inviteeEmail || submitting) ? '#9ca3af' : (pageData.user.brandColor || '#3b82f6'),
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: (!inviteeName || !inviteeEmail || submitting) ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? 'Booking...' : 'Confirm Booking'}
            </button>
          </div>
        )}

        {/* Confirmation */}
        {step === 'confirmed' && bookingConfirmation && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '40px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 25px',
              backgroundColor: '#dcfce7',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ fontSize: '2.5rem' }}>✓</span>
            </div>

            <h2 style={{ margin: '0 0 10px' }}>Booking Confirmed!</h2>
            <p style={{ margin: '0 0 25px', color: '#666' }}>
              A confirmation email has been sent to {inviteeEmail}
            </p>

            <div style={{ padding: '20px', backgroundColor: '#f3f4f6', borderRadius: '8px', marginBottom: '25px' }}>
              <p style={{ margin: 0, fontWeight: '500' }}>{selectedEvent?.title}</p>
              <p style={{ margin: '8px 0 0', color: '#374151' }}>
                {new Date(bookingConfirmation.booking.startTime).toLocaleString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </p>
              {bookingConfirmation.booking.meetLink && (
                <a
                  href={bookingConfirmation.booking.meetLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    marginTop: '15px',
                    padding: '10px 20px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    borderRadius: '6px',
                    textDecoration: 'none'
                  }}
                >
                  Join Meeting
                </a>
              )}
            </div>

            <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>
              Add to calendar:{' '}
              <a href={bookingConfirmation.calendarLinks?.google} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>Google</a>
              {' | '}
              <a href={bookingConfirmation.calendarLinks?.outlook} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>Outlook</a>
            </p>
          </div>
        )}

        {/* Branding */}
        {!pageData.hideBranding && (
          <div style={{ marginTop: '40px', textAlign: 'center' }}>
            <a
              href="https://cal.larryshelp.org"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#9ca3af', fontSize: '0.875rem', textDecoration: 'none' }}
            >
              Powered by cal.larryshelp.org
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicBookingPage;
